import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Box, Typography, IconButton, TextField, Divider, Alert, Tooltip, Chip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { adminAgentsAPI } from '../../../services/api';
import { traversePromptGraph } from './traversePromptGraph';
import { buildPromptContent, substituteTemplateMarkers } from './promptUtils';

const NODE_COLORS = {
  prompt: '#78909c',
  prompt_secondary: '#e8922a',
  execution_rules: '#b71c1c',
  knowledge_management: '#3a8a5a',
  intelligence_guidelines: '#3a6aaa',
  forms: '#7ab020',
};

const NODE_ICONS = {
  prompt: '◈',
  prompt_secondary: '◈',
  execution_rules: '⚡',
  knowledge_management: '📖',
  intelligence_guidelines: '🧠',
  forms: '⊟',
};

// Splits text into runs of plain text, $class.method tokens, and @AgentName tokens.
// Used to highlight tool calls and sub-agent references inside code fence blocks.
function splitToolCalls(text) {
  const parts = [];
  let last = 0;
  for (const m of text.matchAll(/\$(\w+)\.(\w+)(?:\([^)]*\))?|@(\w+)/gs)) {
    if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) });
    parts.push({ type: m[0].startsWith('@') ? 'agent' : 'tool', value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
  return parts;
}

// Pre-process markdown before passing to ReactMarkdown.
// Handles tilde text and inline tool calls, but only outside code fences.
function preprocessContent(content) {
  const lines = content.split('\n');
  const out = [];
  let inFence = false;
  for (const line of lines) {
    if (/^\s*```/.test(line)) { inFence = !inFence; out.push(line); continue; }
    if (inFence) { out.push(line); continue; }
    // Inline $class.method(...) and @AgentName not already in backticks → wrap in backticks
    const processed = line
      .replace(/(?<!`)\$(\w+\.\w+)(\([^)]*\))?/g, (match) => `\`${match}\``)
      .replace(/(?<!\w)@(\w+)/g, (match) => `\`${match}\``);
    out.push(processed);
  }
  return out.join('\n');
}

// Build ReactMarkdown custom components for a given node colour.
// Block elements get a subtle background tint so they look like text highlights.
function makeComponents(color) {
  const blockSx = {
    bgcolor: `${color}33`,
    borderRadius: 0.5,
    px: 0.75,
    mx: -0.75,
  };

  const renderCode = ({ inline, children }) => {
    const text = String(children);
    if (!inline) {
      const parts = splitToolCalls(text);
      return (
        <pre style={{ background: 'rgba(0,0,0,0.05)', padding: '8px 12px', borderRadius: 4, overflow: 'auto', marginBottom: 8, fontSize: 11, fontFamily: 'monospace' }}>
          <code>
            {parts.map((p, i) =>
              p.type === 'tool'
                ? <span key={i} style={{ background: 'rgba(34,197,94,0.18)', color: '#16a34a', borderRadius: 2, padding: '0 2px', fontWeight: 600 }}>{p.value}</span>
                : p.type === 'agent'
                ? <span key={i} style={{ background: 'rgba(168,85,247,0.18)', color: '#a855f7', borderRadius: 2, padding: '0 2px', fontWeight: 600 }}>{p.value}</span>
                : p.value
            )}
          </code>
        </pre>
      );
    }
    if (/^\$\w+\.\w+/.test(text)) {
      return (
        <span style={{ background: 'rgba(34,197,94,0.15)', color: '#16a34a', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace', fontSize: 11, fontWeight: 500 }}>
          {text}
        </span>
      );
    }
    if (/^@\w+/.test(text)) {
      return (
        <span style={{ background: 'rgba(168,85,247,0.15)', color: '#a855f7', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace', fontSize: 11, fontWeight: 500 }}>
          {text}
        </span>
      );
    }
    return (
      <code style={{ background: 'rgba(6,182,212,0.12)', color: '#0891b2', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace', fontSize: 11 }}>
        {text}
      </code>
    );
  };

  return {
    p: ({ children }) => <Box component="p" sx={{ ...blockSx, mt: 0, mb: 0.75 }}>{children}</Box>,
    h1: ({ children }) => <Box component="h1" sx={{ ...blockSx, fontSize: 16, fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>{children}</Box>,
    h2: ({ children }) => <Box component="h2" sx={{ ...blockSx, fontSize: 14, fontWeight: 'bold', mt: 1.5, mb: 0.5 }}>{children}</Box>,
    h3: ({ children }) => <Box component="h3" sx={{ ...blockSx, fontSize: 13, fontWeight: 'bold', mt: 1, mb: 0.5 }}>{children}</Box>,
    h4: ({ children }) => <Box component="h4" sx={{ ...blockSx, fontSize: 12, fontWeight: 'bold', mt: 1, mb: 0.5 }}>{children}</Box>,
    ul: ({ children }) => <Box component="ul" sx={{ ...blockSx, pl: 2.5, mb: 0.75 }}>{children}</Box>,
    ol: ({ children }) => <Box component="ol" sx={{ ...blockSx, pl: 2.5, mb: 0.75 }}>{children}</Box>,
    li: ({ children }) => <Box component="li" sx={{ mb: 0.25 }}>{children}</Box>,
    blockquote: ({ children }) => (
      <Box component="blockquote" sx={{ ...blockSx, borderLeft: '3px solid', borderColor: 'divider', pl: 1.5, color: 'text.secondary', my: 0.75 }}>
        {children}
      </Box>
    ),
    hr: () => <Box component="hr" sx={{ border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 1.5 }} />,
    // ~~text~~ strikethrough — make it more visible than default
    del: ({ children }) => (
      <span style={{ background: 'rgba(180,0,0,0.1)', color: '#990000', borderRadius: 3, padding: '1px 4px', textDecoration: 'line-through' }}>
        {children}
      </span>
    ),
    code: renderCode,
  };
}

function KeyValueEditor({ title, pairs, onChange }) {
  const add = () => onChange([...pairs, { key: '', value: '' }]);
  const remove = (i) => onChange(pairs.filter((_, idx) => idx !== i));
  const update = (i, field, val) => onChange(pairs.map((p, idx) => idx === i ? { ...p, [field]: val } : p));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="overline" sx={{ fontSize: 9, flex: 1 }}>{title}</Typography>
        <IconButton size="small" onClick={add}><AddIcon sx={{ fontSize: 14 }} /></IconButton>
      </Box>
      {pairs.map((p, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField size="small" placeholder="field" value={p.key} onChange={(e) => update(i, 'key', e.target.value)} inputProps={{ style: { fontSize: 10 } }} sx={{ flex: 1 }} />
          <TextField size="small" placeholder="value" value={p.value} onChange={(e) => update(i, 'value', e.target.value)} inputProps={{ style: { fontSize: 10 } }} sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => remove(i)}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
        </Box>
      ))}
    </Box>
  );
}

function pairsToObject(pairs) {
  const obj = {};
  for (const { key, value } of pairs) {
    const k = key.trim();
    if (!k) continue;
    const parts = k.split('.');
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (typeof cur[parts[i]] !== 'object' || cur[parts[i]] === null) cur[parts[i]] = {};
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
  }
  return obj;
}

function AssembledPrompt({ fragments }) {
  if (fragments.length === 0) {
    return (
      <Typography sx={{ color: 'text.disabled', fontSize: 10 }}>
        Nothing to preview — add prompt nodes to the canvas.
      </Typography>
    );
  }

  const resolveColorKey = (f) =>
    f.nodeType === 'prompt' && f.promptType !== 'base' ? 'prompt_secondary' : f.nodeType;

  // Legend: unique color keys present
  const seenTypes = [];
  const seen = new Set();
  for (const f of fragments) {
    const key = resolveColorKey(f);
    if (!seen.has(key)) { seen.add(key); seenTypes.push({ ...f, _colorKey: key }); }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {seenTypes.map((f) => {
          const colorKey = f._colorKey || f.nodeType;
          const color = NODE_COLORS[colorKey] || '#666';
          return (
            <Chip
              key={colorKey}
              label={`${NODE_ICONS[colorKey] || '◈'} ${colorKey.replace(/_/g, ' ')}`}
              size="small"
              sx={{ fontSize: 9, height: 18, bgcolor: `${color}20`, color, border: `1px solid ${color}55` }}
            />
          );
        })}
        <Chip
          label="$tool.call"
          size="small"
          sx={{ fontSize: 9, height: 18, bgcolor: 'rgba(34,197,94,0.12)', color: '#16a34a', border: '1px solid rgba(34,197,94,0.3)' }}
        />
        <Chip
          label="@SubAgent"
          size="small"
          sx={{ fontSize: 9, height: 18, bgcolor: 'rgba(168,85,247,0.12)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.3)', fontFamily: 'monospace' }}
        />
        <Chip
          label="`code`"
          size="small"
          sx={{ fontSize: 9, height: 18, bgcolor: 'rgba(6,182,212,0.1)', color: '#0891b2', border: '1px solid rgba(6,182,212,0.3)', fontFamily: 'monospace' }}
        />
      </Box>

      <Box sx={{ fontSize: 12, lineHeight: 1.7, color: 'text.primary' }}>
        {fragments.map((f) => {
          const color = NODE_COLORS[resolveColorKey(f)] || '#666';
          const tooltipTitle = (
            <span style={{ fontSize: 11 }}>
              {NODE_ICONS[f.nodeType] || '◈'} <strong>{f.label}</strong>
              <br />
              <span style={{ opacity: 0.8 }}>{f.nodeType.replace(/_/g, ' ')}</span>
            </span>
          );
          const components = makeComponents(color);
          const processed = preprocessContent(f.content);
          return (
            <Tooltip
              key={f.nodeId}
              title={tooltipTitle}
              placement="top"
              followCursor
              arrow
              disableInteractive
              enterDelay={200}
            >
              <Box sx={{ cursor: 'default', '& > *:first-of-type': { mt: 0 } }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                  {processed}
                </ReactMarkdown>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

export default function PromptVisualizerPanel({ nodes, edges, onOpen }) {
  const [open, setOpen] = useState(false);

  const handleOpen = () => { setOpen(true); onOpen?.(); };
  const [ticketPairs, setTicketPairs] = useState([]);
  const [agentDataPairs, setAgentDataPairs] = useState([]);
  const [panelWidth, setPanelWidth] = useState(320);
  const dragState = useRef(null);

  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startWidth: panelWidth };
    const onMove = (e) => {
      if (!dragState.current) return;
      const delta = dragState.current.startX - e.clientX;
      setPanelWidth(Math.max(240, Math.min(700, dragState.current.startWidth + delta)));
    };
    const onUp = () => {
      dragState.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  const rootCount = useMemo(() => {
    const promptNodes = nodes.filter((n) => n.type === 'prompt');
    const promptNodeIds = new Set(promptNodes.map((n) => n.id));
    const targetIds = new Set(
      edges.filter((e) => promptNodeIds.has(e.source) && promptNodeIds.has(e.target)).map((e) => e.target)
    );
    return promptNodes.filter((n) => !targetIds.has(n.id)).length;
  }, [nodes, edges]);

  // Server template contents for global markers (e.g. a forms node holding {{FORMS}}),
  // so the rendered prompt shows what the backend will actually assemble.
  const [templates, setTemplates] = useState({});
  const usesFormsMarker = useMemo(
    () => nodes.some((n) => (n.data?.content || '').includes('{{FORMS}}')),
    [nodes]
  );
  useEffect(() => {
    if (!usesFormsMarker || templates.FORMS) return;
    let cancelled = false;
    adminAgentsAPI.getPromptTemplate('forms')
      .then(({ content }) => {
        if (!cancelled) setTemplates((prev) => ({ ...prev, FORMS: content }));
      })
      .catch(() => {}); // marker stays visible in the preview if the fetch fails
    return () => { cancelled = true; };
  }, [usesFormsMarker, templates.FORMS]);

  const fragments = useMemo(() => {
    const contextData = {
      ticket: pairsToObject(ticketPairs),
      agent_data: pairsToObject(agentDataPairs),
    };
    return traversePromptGraph(nodes, edges, contextData).map((f) => ({
      ...f,
      content: substituteTemplateMarkers(buildPromptContent(f.content, nodes), templates),
    }));
  }, [nodes, edges, ticketPairs, agentDataPairs, templates]);

  if (!open) {
    return (
      <Box sx={{ width: 24, flexShrink: 0, borderLeft: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 1 }}>
        <IconButton size="small" onClick={handleOpen} title="Open Prompt Visualizer">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <>
      <Box
        onMouseDown={handleDividerMouseDown}
        sx={{ width: 4, flexShrink: 0, cursor: 'col-resize', bgcolor: 'divider', '&:hover': { bgcolor: 'primary.main' }, transition: 'background-color 0.15s' }}
      />
      <Box sx={{ width: panelWidth, flexShrink: 0, borderLeft: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" sx={{ fontSize: 9, flex: 1 }}>Prompt Visualizer</Typography>
          <IconButton size="small" onClick={() => setOpen(false)}><ChevronRightIcon fontSize="small" /></IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rootCount !== 1 && (
            <Alert severity="warning" sx={{ fontSize: 10 }}>
              {rootCount === 0 ? 'No root prompt node found.' : `${rootCount} root nodes — connect all but one.`}
            </Alert>
          )}
          <KeyValueEditor title="ticket.*" pairs={ticketPairs} onChange={setTicketPairs} />
          <Divider />
          <KeyValueEditor title="agent_data.*" pairs={agentDataPairs} onChange={setAgentDataPairs} />
          <Divider />
          <AssembledPrompt fragments={fragments} />
        </Box>
      </Box>
    </>
  );
}
